// Which game the players agreed to play (§26 F1). Rides in the opening command
// and lives on `GameState`, so a replay and a Restart both get the same game.
// Absent is today's game: stored only when non-default, hashed only when stored.
//
// One dial is left: `spawn`. The material dials (`materials`, `bills`, `loot`,
// `diamondBonus`) retired with the A/B arms (§31 R3) — the game is one game.

export interface Ruleset {
  /** How camps arrive: on a clock, or when a front is beaten (today). F10. */
  spawn: "clock" | "defeat";
}

/** Any subset of the dials; an omitted one is the default. */
export type RulesetChoice = Partial<Ruleset>;

export const SPAWN_MODES = ["clock", "defeat"] as const;

/** Today's game. */
export const DEFAULT_RULESET: Ruleset = { spawn: "defeat" };

/**
 * The full ruleset a choice means, or null for one this build does not know.
 * Refused, never defaulted: defaulting is two clients replaying one log onto
 * two different games. Absent is the default and is not unknown — and so is a
 * key this build has no dial for, which is a log from another build.
 */
export function normalizeRuleset(raw: RulesetChoice | undefined): Ruleset | null {
  if (raw === undefined) return { ...DEFAULT_RULESET };
  if (typeof raw !== "object" || raw === null) return null;
  // Widened on purpose: the wire promises nothing, so the check is on strings.
  if (Object.keys(raw).some((key) => key !== "spawn")) return null;
  const spawn = raw.spawn ?? DEFAULT_RULESET.spawn;
  if (!(SPAWN_MODES as readonly string[]).includes(spawn as string)) return null;
  return { spawn };
}

/** Is this exactly the shipped game? The default is never stored or hashed. */
export const isDefaultRuleset = (ruleset: Ruleset): boolean =>
  ruleset.spawn === DEFAULT_RULESET.spawn;

/** The one reader, so "absent means default" is decided in one place. */
export const rulesetOf = (state: { ruleset?: Ruleset }): Ruleset =>
  state.ruleset ?? DEFAULT_RULESET;

/** A stable line for the hash — only ever asked about a non-default ruleset. */
export const rulesetKey = (ruleset: Ruleset): string => ruleset.spawn;
